import { useMemo } from 'react';
import { useCarousel } from './carousel';
import { cn } from '../../utils/cn';

const DOT_SIZE_REM = 0.5;
const GAP_SIZE_REM = 0.25;

/**
 * The pagination component for the carousel.
 * @see {@link Carousel}
 */
export function CarouselPagination({
    className,
    maxDots = 5,
}: {
    className?: string;
    /**
     * The maximum number of dots to display. Defaults to `5`.
     */
    maxDots?: number;
}) {
    const { currentSlide, totalSlides } = useCarousel();
    const visibleDots = useMemo(() => {
        return Math.min(maxDots, totalSlides);
    }, [maxDots, totalSlides]);
    const translateXRem = useMemo(() => {
        const fullDotWidthRem = DOT_SIZE_REM + GAP_SIZE_REM;
        const halfVisibleDots = Math.floor(visibleDots / 2);
        // Calculate start dot properly
        let startDot = 0;
        if (currentSlide <= halfVisibleDots) {
            startDot = 0;
        } else if (currentSlide >= totalSlides - halfVisibleDots - 1) {
            startDot = totalSlides - visibleDots;
        } else {
            startDot = currentSlide - halfVisibleDots;
        }
        return startDot * fullDotWidthRem;
    }, [currentSlide, totalSlides, visibleDots]);

    return (
        <div
            className={cn(
                'z-10 flex items-center justify-center',
                // There may be clipping on Firefox with overflow-hidden.
                totalSlides > visibleDots && 'overflow-hidden',
                className
            )}
            style={{
                width: `${DOT_SIZE_REM * visibleDots + GAP_SIZE_REM * (visibleDots - 1)}rem`,
            }}
            data-slot="carousel-pagination"
        >
            <div
                className="flex items-center transition-transform duration-500 ease-in-out"
                style={{
                    transform: `translateX(-${translateXRem}rem)`,
                    gap: `${GAP_SIZE_REM}rem`,
                }}
            >
                {[...Array(totalSlides).keys()].map((key) => (
                    <div
                        key={key}
                        className={cn(
                            'rounded-full bg-white transition-opacity duration-500 ease-in-out',
                            currentSlide === key ? 'opacity-100' : 'opacity-40'
                        )}
                        style={{
                            width: `${DOT_SIZE_REM}rem`,
                            height: `${DOT_SIZE_REM}rem`,
                        }}
                    />
                ))}
            </div>
        </div>
    );
}
